import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomsCalculation, SearchCustomsCalculationsRequest } from '../../../../services/api/customs-calculations';
import { PageResponse } from '../../../../services/api/page-response';
import { PaginationComponent } from '../../pagination/pagination.component';
import { LanguageService } from '../../../../services/i18n/language';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-supplies-list',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, PaginationComponent],
  templateUrl: './supplies-list.html',
  styleUrl: './supplies-list.scss'
})
export class SuppliesListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() calculations: CustomsCalculation[] = [];
  @Input() calculationsPage: PageResponse<CustomsCalculation> | null = null;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() title = '';
  @Input() subtitle = '';
  @Input() emptyText = '';

  @Output() viewCalculation = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() filterChange = new EventEmitter<SearchCustomsCalculationsRequest>();

  search = '';
  dateFrom: string | null = null;
  dateTo: string | null = null;

  totalGoodsCost = 0;
  totalCustomsPayments = 0;

  searchText = 'Поиск';
  searchPlaceholderText = 'Название расчёта, товар, инвойс...';
  periodText = 'Период создания';
  applyText = 'Применить';
  clearText = 'Очистить';
  colNumberText = '№ поставки';
  colProductText = 'Товар / Инвойс';
  colRouteText = 'Маршрут';
  colStatusText = 'Статус';
  colCreatedText = 'Создана';
  colCostText = 'Стоимость товара';
  openText = 'Открыть';
  invoiceText = 'Инвойс';
  summaryTitleText = 'Общая сводка';
  suppliesCountText = 'поставок';
  goodsCostTitleText = 'Стоимость товаров (на странице)';
  customsPaymentsTitleText = 'Таможенные платежи (на странице)';
  loadingText = 'Загрузка...';
  private statusCreatedText = 'Создан';
  private statusInProcessingText = 'В обработке';
  private statusProcessedText = 'Обработан';
  private andMoreKey = 'common.supplies.andMore';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    const t = (key: string, fallback: string): string => {
      const value = this.languageService.translate(`common.supplies.${key}`);
      return !value || value === `common.supplies.${key}` ? fallback : value;
    };
    this.searchText = t('search', this.searchText);
    this.searchPlaceholderText = t('searchPlaceholder', this.searchPlaceholderText);
    this.periodText = t('period', this.periodText);
    this.applyText = t('apply', this.applyText);
    this.clearText = t('clear', this.clearText);
    this.colNumberText = t('colNumber', this.colNumberText);
    this.colProductText = t('colProduct', this.colProductText);
    this.colRouteText = t('colRoute', this.colRouteText);
    this.colStatusText = t('colStatus', this.colStatusText);
    this.colCreatedText = t('colCreated', this.colCreatedText);
    this.colCostText = t('colCost', this.colCostText);
    this.openText = t('open', this.openText);
    this.invoiceText = t('invoice', this.invoiceText);
    this.summaryTitleText = t('summaryTitle', this.summaryTitleText);
    this.suppliesCountText = t('suppliesCount', this.suppliesCountText);
    this.goodsCostTitleText = t('goodsCostTitle', this.goodsCostTitleText);
    this.customsPaymentsTitleText = t('customsPaymentsTitle', this.customsPaymentsTitleText);
    this.loadingText = this.languageService.translate('common.loading') || this.loadingText;
    this.statusCreatedText = t('statusCreated', this.statusCreatedText);
    this.statusInProcessingText = t('statusInProcessing', this.statusInProcessingText);
    this.statusProcessedText = t('statusProcessed', this.statusProcessedText);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['calculations']) {
      this.recalculateTotals();
    }
  }

  private recalculateTotals(): void {
    this.totalGoodsCost = this.calculations.reduce((sum, c) => sum + (c.totalProductCostKzt || 0), 0);
    this.totalCustomsPayments = this.calculations.reduce((sum, c) => sum + (c.totalCustomsPayments || 0), 0);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  applyFilters(): void {
    const request: SearchCustomsCalculationsRequest = {
      title: this.search?.trim() || undefined,
      dateFrom: this.dateFrom || undefined,
      dateTo: this.dateTo || undefined
    };
    this.filterChange.emit(request);
  }

  clearFilters(): void {
    this.search = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.search?.trim() || this.dateFrom || this.dateTo);
  }

  onOpen(id: number): void {
    this.viewCalculation.emit(id);
  }

  private productNames(calculation: CustomsCalculation): string[] {
    return (calculation.products || [])
      .map(p => p.nameForBroker || p.nameOriginal)
      .filter((name): name is string => !!name);
  }

  getProductsSummary(calculation: CustomsCalculation): string {
    const names = this.productNames(calculation);
    if (names.length === 0) {
      return calculation.title || '—';
    }
    if (names.length === 1) {
      return names[0];
    }
    const andMore = this.languageService.translate(this.andMoreKey, { count: names.length - 1 });
    const suffix = !andMore || andMore === this.andMoreKey ? `и ещё ${names.length - 1}` : andMore;
    return `${names[0]} ${suffix}`;
  }

  /** Полный текст для нативного тултипа (title) — все товары, каждый с новой строки. */
  getProductsFullText(calculation: CustomsCalculation): string {
    const names = this.productNames(calculation);
    if (names.length === 0) {
      return calculation.title || '';
    }
    return names.join('\n');
  }

  getInvoiceNumber(calculation: CustomsCalculation): string | null {
    return calculation.invoiceData?.invoiceNumber || null;
  }

  hasRoute(calculation: CustomsCalculation): boolean {
    return !!(calculation.fromCountryName || calculation.fromCity
      || calculation.toCountryName || calculation.toCity);
  }

  getRouteFrom(calculation: CustomsCalculation): string {
    const parts = [calculation.fromCountryName, calculation.fromCity].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  getRouteTo(calculation: CustomsCalculation): string {
    const parts = [calculation.toCountryName, calculation.toCity].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  statusLabel(status?: string): string {
    switch (status) {
      case 'IN_PROCESSING': return this.statusInProcessingText;
      case 'PROCESSED': return this.statusProcessedText;
      case 'CREATED':
      default: return this.statusCreatedText;
    }
  }
}
